import React, { type FC } from 'react'

import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  Flex,
  FormElement,
  InputText,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
  Select,
} from 'mailup-web-components'

import { panelEditContactsChangeFieldCallback } from 'Pages/RecipientsList/recoil/callbacks/editContacts'
import { countryCodesState } from 'Pages/RecipientsList/recoil/state/common'
import {
  panelEditContactsIsEmailValidState,
  panelEditContactsIsMobileValidState,
  panelEditContactsState,
} from 'Pages/RecipientsList/recoil/state/editContacts'

const BoxEditContacts: FC = () => {
  const intl = useIntl()

  const {
    Email,
    MobilePrefix,
    MobileNumber,
    EmailDisabled,
    EmailRequired,
    SMSDisabled,
    SMSRequired,
    isDirty,
  } = useRecoilValue(panelEditContactsState)

  const { items: countryCodes } = useRecoilValue(countryCodesState)

  const isValidEmail = useRecoilValue(panelEditContactsIsEmailValidState)
  const isValidMobile = useRecoilValue(panelEditContactsIsMobileValidState)

  const changeField = useRecoilCallback(
    panelEditContactsChangeFieldCallback,
    [],
  )

  const handleChangeField =
    (f: 'Email' | 'MobilePrefix' | 'MobileNumber') =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      void changeField(f, e.target.value)
    }

  const handleChangePrefix = (v: string | undefined): void => {
    void changeField('MobilePrefix', v)
  }

  const emailFormatFeedback = isDirty && !isValidEmail
  const mobileFormatFeedback = isDirty && !isValidMobile

  const emailFeedback = EmailDisabled
    ? undefined
    : EmailRequired && Email.length === 0
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : emailFormatFeedback
        ? intl.formatMessage({ id: 'Load.Common.WrongFormatWarning' })
        : undefined

  const mobileFeedback = SMSDisabled
    ? undefined
    : SMSRequired && (MobilePrefix.length === 0 || MobileNumber.length === 0)
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : mobileFormatFeedback
        ? intl.formatMessage({ id: 'Load.Common.ValidationWrongFormat' })
        : undefined

  return (
    <Flex flexDirection="column" gap="24px">
      <MessageBox variant={MESSAGE_BOX_VARIANT_HELP}>
        {intl.formatMessage({ id: 'Load.RecipientsList.EditContactsInfo' })}
      </MessageBox>

      <FormElement
        label={intl.formatMessage({ id: 'Load.RecipientsList.ColumnEmail' })}
        feedback={emailFeedback}
        feedbackVariant={emailFeedback !== undefined ? 'error' : undefined}
        required={EmailRequired}
      >
        <InputText
          id="Email"
          name="Email"
          value={Email}
          onChange={handleChangeField('Email')}
          data-mu-test="edit-contacts-email"
          variant={emailFeedback !== undefined ? 'error' : undefined}
          maxLength={100}
          disabled={EmailDisabled}
        />
      </FormElement>

      <FormElement
        label={intl.formatMessage({
          id: 'Load.RecipientsList.ColumnMobilePrefix',
        })}
        feedback={mobileFeedback}
        feedbackVariant={mobileFeedback !== undefined ? 'error' : undefined}
        required={SMSRequired}
      >
        <Select
          value={MobilePrefix}
          options={countryCodes}
          onChange={handleChangePrefix}
          data-mu-test="edit-contacts-mobile-prefix"
          error={mobileFeedback !== undefined}
          placeholder={intl.formatMessage({
            id: 'Load.MailupCommon.Select',
          })}
          disabled={SMSDisabled}
        />
      </FormElement>

      <FormElement
        label={intl.formatMessage({
          id: 'Load.RecipientsList.ColumnMobileNumber',
        })}
        feedback={mobileFeedback}
        feedbackVariant={mobileFeedback !== undefined ? 'error' : undefined}
        required={SMSRequired}
      >
        <InputText
          id="MobileNumber"
          name="MobileNumber"
          value={MobileNumber}
          onChange={handleChangeField('MobileNumber')}
          data-mu-test="edit-contacts-mobile-number"
          variant={mobileFeedback !== undefined ? 'error' : undefined}
          disabled={SMSDisabled}
        />
      </FormElement>
    </Flex>
  )
}

export default BoxEditContacts
